import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { GripVertical, Pencil, Trash2, Plus, HelpCircle, Eye, Flame, Lightbulb, ThumbsUp, ThumbsDown, FolderPlus, Check, X, Megaphone, MoveRight, Search } from "lucide-react";
import { formatDate, trFold, SearchClear } from "@/components/shared";
import { toast } from "sonner";

const AUDIENCES = { supplier: "Tedarikçi", customer: "Müşteri", both: "Her İkisi" };
const audienceCls = {
  supplier: "border-slate-300 text-slate-600",
  customer: "border-blue-300 text-blue-600",
  both: "border-emerald-300 text-emerald-600",
};

export default function AdminFaqPage() {
  const [faqs, setFaqs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [audience, setAudience] = useState("supplier");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [newCatName, setNewCatName] = useState("");
  const [editCat, setEditCat] = useState(null);
  const [editCatName, setEditCatName] = useState("");
  const [selected, setSelected] = useState([]);
  const [bulkTarget, setBulkTarget] = useState(undefined);
  const [catDragIdx, setCatDragIdx] = useState(null);
  const [catOverIdx, setCatOverIdx] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [fromSuggestion, setFromSuggestion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [q, setQ] = useState("");

  const load = () => api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {}).finally(() => setLoaded(true));
  const loadSuggestions = () => api.get("/faq-suggestions").then((r) => setSuggestions(r.data)).catch(() => {});
  const loadCategories = () => api.get("/faq-categories").then((r) => setCategories(r.data)).catch(() => {});
  useEffect(() => { load(); loadSuggestions(); loadCategories(); }, []);

  const catName = (id) => categories.find((c) => c.id === id)?.name;

  const addCategory = async () => {
    if (newCatName.trim().length < 2) { toast.error("Kategori adı en az 2 karakter olmalıdır"); return; }
    try {
      await api.post("/faq-categories", { name: newCatName });
      toast.success("Kategori eklendi");
      setNewCatName("");
      loadCategories();
    } catch (e) { toast.error(apiError(e)); }
  };

  const renameCategory = async () => {
    try {
      await api.put(`/faq-categories/${editCat}`, { name: editCatName });
      toast.success("Kategori güncellendi");
      setEditCat(null);
      loadCategories();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteCategory = async (id) => {
    try {
      await api.delete(`/faq-categories/${id}`);
      toast.success("Kategori silindi");
      setCategories((c) => c.filter((x) => x.id !== id));
    } catch (e) { toast.error(apiError(e)); }
  };

  const maxViews = Math.max(0, ...faqs.map((f) => f.views || 0));

  const openNew = () => { setEditing(null); setQuestion(""); setAnswer(""); setAudience("supplier"); setCategoryId(categories[0]?.id || ""); setFromSuggestion(null); setDialogOpen(true); };
  const openEdit = (f) => { setEditing(f); setQuestion(f.question); setAnswer(f.answer); setAudience(f.audience || "supplier"); setCategoryId(f.category_id || ""); setFromSuggestion(null); setDialogOpen(true); };

  const convertSuggestion = (s) => {
    setEditing(null);
    setQuestion(s.question);
    setAnswer("");
    setAudience(s.role === "customer" ? "customer" : "supplier");
    setCategoryId(categories[0]?.id || "");
    setFromSuggestion(s.id);
    setDialogOpen(true);
  };

  const dismissSuggestion = async (id) => {
    try {
      await api.delete(`/faq-suggestions/${id}`);
      setSuggestions((s) => s.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const bulkMove = async () => {
    if (!bulkTarget) { toast.error("Hedef kategori seçin"); return; }
    const catId = bulkTarget === "none" ? "" : bulkTarget;
    try {
      await api.post("/faqs/bulk-move", { ids: selected, category_id: catId });
      setFaqs((fs) => fs.map((f) => (selected.includes(f.id) ? { ...f, category_id: catId } : f)));
      toast.success(`${selected.length} soru taşındı`);
      setSelected([]);
      setBulkTarget(undefined);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleLanding = async (f) => {
    try {
      await api.post(`/faqs/${f.id}/landing`, { on_landing: !f.on_landing });
      setFaqs((fs) => fs.map((x) => (x.id === f.id ? { ...x, on_landing: !f.on_landing } : x)));
      toast.success(!f.on_landing ? "Tanıtım sayfasına eklendi" : "Tanıtım sayfasından kaldırıldı");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const onCatDrop = (i) => {
    setCatOverIdx(null);
    if (catDragIdx === null || catDragIdx === i) { setCatDragIdx(null); return; }
    const arr = [...categories];
    const [moved] = arr.splice(catDragIdx, 1);
    arr.splice(i, 0, moved);
    setCategories(arr);
    setCatDragIdx(null);
    api.post("/faq-categories/reorder", { ids: arr.map((c) => c.id) })
      .then(() => toast.success("Kategori sıralaması kaydedildi"))
      .catch((e) => { toast.error(apiError(e)); loadCategories(); });
  };

  const moveCategory = async (f, newCatId) => {
    const catId = newCatId === "none" ? "" : newCatId;
    if (catId === (f.category_id || "")) return;
    try {
      await api.put(`/faqs/${f.id}`, { question: f.question, answer: f.answer, audience: f.audience || "supplier", category_id: catId });
      setFaqs((fs) => fs.map((x) => (x.id === f.id ? { ...x, category_id: catId } : x)));
      toast.success(catId ? `"${catName(catId)}" kategorisine taşındı` : "Kategorisiz olarak ayarlandı");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const save = async () => {
    if (question.trim().length < 3 || answer.trim().length < 3) {
      toast.error("Soru ve cevap en az 3 karakter olmalıdır");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/faqs/${editing.id}`, { question, answer, audience, category_id: categoryId });
        toast.success("Soru güncellendi");
      } else {
        await api.post("/faqs", { question, answer, audience, category_id: categoryId });
        toast.success("Soru eklendi");
        if (fromSuggestion) {
          await api.delete(`/faq-suggestions/${fromSuggestion}`).catch(() => {});
          setSuggestions((s) => s.filter((x) => x.id !== fromSuggestion));
          setFromSuggestion(null);
        }
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/faqs/${toDelete.id}`);
      toast.success("Soru silindi");
      setFaqs((f) => f.filter((x) => x.id !== toDelete.id));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setToDelete(null);
    }
  };

  const onDrop = (i) => {
    setOverIdx(null);
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); return; }
    const arr = [...faqs];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(i, 0, moved);
    setFaqs(arr);
    setDragIdx(null);
    api.post("/faqs/reorder", { ids: arr.map((f) => f.id) })
      .then(() => toast.success("Sıralama kaydedildi"))
      .catch((e) => { toast.error(apiError(e)); load(); });
  };

  return (
    <div className="max-w-4xl" data-testid="admin-faq-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-1">Yardım Merkezi</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter">Sıkça Sorulan Sorular</h1>
          <p className="text-sm text-slate-500 mt-2">Her soru için hedef kitleyi (Tedarikçi / Müşteri / Her İkisi) seçebilir, sıralamayı sürükle-bırak ile değiştirebilirsiniz.</p>
        </div>
        <Button onClick={openNew} data-testid="add-faq-button" className="rounded-none bg-slate-900 text-white hover:bg-slate-700 gap-2 h-10 font-semibold transition-colors duration-200 shrink-0">
          <Plus size={15} /> Yeni Soru
        </Button>
      </div>

      <div className="border border-slate-200 bg-white p-4 mb-6" data-testid="faq-categories-panel">
        <div className="font-semibold text-sm flex items-center gap-2 mb-3">
          <FolderPlus size={15} /> Kategoriler
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {categories.map((c, i) => {
            const count = faqs.filter((f) => f.category_id === c.id).length;
            return editCat === c.id ? (
              <div key={c.id} className="flex items-center gap-1 border border-slate-900 px-2 py-1" data-testid={`cat-edit-row-${c.id}`}>
                <Input value={editCatName} onChange={(e) => setEditCatName(e.target.value)} className="rounded-none border-0 h-7 text-xs w-40 px-1 focus-visible:ring-0" data-testid={`cat-edit-input-${c.id}`} autoFocus />
                <button type="button" onClick={renameCategory} data-testid={`cat-edit-save-${c.id}`} className="text-emerald-600 hover:text-emerald-700 p-0.5"><Check size={14} /></button>
                <button type="button" onClick={() => setEditCat(null)} data-testid={`cat-edit-cancel-${c.id}`} className="text-slate-400 hover:text-slate-600 p-0.5"><X size={14} /></button>
              </div>
            ) : (
              <div
                key={c.id}
                draggable
                onDragStart={() => setCatDragIdx(i)}
                onDragOver={(e) => { e.preventDefault(); setCatOverIdx(i); }}
                onDragLeave={() => setCatOverIdx((o) => (o === i ? null : o))}
                onDrop={() => onCatDrop(i)}
                onDragEnd={() => { setCatDragIdx(null); setCatOverIdx(null); }}
                className={`flex items-center gap-1.5 border px-3 py-1.5 text-xs font-medium cursor-grab active:cursor-grabbing transition-colors duration-150 ${
                  catOverIdx === i && catDragIdx !== null && catDragIdx !== i ? "border-slate-900 border-dashed bg-white" : "border-slate-200 bg-slate-50"
                } ${catDragIdx === i ? "opacity-50" : ""}`}
                data-testid={`cat-chip-${c.id}`}
              >
                <GripVertical size={12} className="text-slate-300" />
                {c.name} <span className="text-slate-400">({count})</span>
                <button type="button" onClick={() => { setEditCat(c.id); setEditCatName(c.name); }} data-testid={`cat-rename-${c.id}`} className="text-slate-400 hover:text-slate-900 ml-1 transition-colors duration-150" title="Yeniden adlandır"><Pencil size={12} /></button>
                <button type="button" onClick={() => deleteCategory(c.id)} data-testid={`cat-delete-${c.id}`} className="text-slate-400 hover:text-red-500 transition-colors duration-150" title="Sil"><Trash2 size={12} /></button>
              </div>
            );
          })}
          {categories.length === 0 && <span className="text-xs text-slate-400">Henüz kategori yok.</span>}
        </div>
        <div className="flex gap-2">
          <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addCategory(); }} placeholder="Yeni kategori adı..." data-testid="new-category-input" className="rounded-none border-slate-200 h-9 max-w-xs" />
          <Button onClick={addCategory} data-testid="add-category-button" variant="outline" className="rounded-none h-9 border-slate-200 hover:bg-slate-100 gap-1.5 font-semibold text-xs transition-colors duration-200">
            <Plus size={13} /> Ekle
          </Button>
        </div>
      </div>

      {suggestions.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 p-4 mb-6" data-testid="faq-suggestions-panel">
          <div className="font-semibold text-sm flex items-center gap-2 mb-3">
            <Lightbulb size={15} className="text-amber-600" /> Soru Önerileri ({suggestions.length})
          </div>
          <div className="space-y-2">
            {suggestions.map((s) => (
              <div key={s.id} className="bg-white border border-amber-200 p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`faq-suggestion-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{s.question}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{s.company_name} · {s.role === "supplier" ? "Tedarikçi" : "Müşteri"} · {formatDate(s.created_at)}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" onClick={() => convertSuggestion(s)} data-testid={`convert-suggestion-${s.id}`} className="rounded-none h-8 bg-slate-900 text-white hover:bg-slate-700 text-xs font-semibold transition-colors duration-200">
                    Soruya Dönüştür
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => dismissSuggestion(s.id)} data-testid={`dismiss-suggestion-${s.id}`} className="rounded-none h-8 border-slate-200 hover:bg-slate-100 text-xs transition-colors duration-200">
                    Yoksay
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selected.length > 0 && (
        <div className="border border-slate-900 bg-slate-900 text-white p-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3 sticky top-2 z-10" data-testid="bulk-move-bar">
          <div className="text-sm font-semibold">{selected.length} soru seçildi</div>
          <div className="flex items-center gap-2 sm:ml-auto">
            <Select value={bulkTarget || undefined} onValueChange={setBulkTarget}>
              <SelectTrigger className="rounded-none h-9 border-slate-600 bg-slate-800 text-white text-xs w-[180px]" data-testid="bulk-target-select">
                <SelectValue placeholder="Hedef kategori seç..." />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="none">Kategorisiz</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`bulk-target-option-${c.id}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={bulkMove} data-testid="bulk-move-button" className="rounded-none h-9 bg-white text-slate-900 hover:bg-slate-200 gap-1.5 font-semibold text-xs transition-colors duration-200">
              <MoveRight size={13} /> Taşı
            </Button>
            <Button variant="outline" onClick={() => setSelected([])} data-testid="bulk-cancel-button" className="rounded-none h-9 border-slate-600 bg-transparent text-white hover:bg-slate-800 text-xs transition-colors duration-200">
              Vazgeç
            </Button>
          </div>
        </div>
      )}

      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input
          data-testid="faq-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Soru, cevap veya kategori ara..."
          className="rounded-none bg-white border-slate-200 pl-9 pr-9"
        />
        <SearchClear value={q} onClear={() => setQ("")} testid="faq-search-clear" />
      </div>

      {faqs.length === 0 ? (
        <div className="border border-slate-200 bg-white px-4 py-14 text-center text-slate-500" data-testid="faq-empty">
          {loaded ? "Henüz soru eklenmedi. \"Yeni Soru\" butonu ile başlayın." : "Yükleniyor..."}
        </div>
      ) : (() => {
        const needle = trFold(q.trim());
        const catName = (cid) => (categories.find((c) => c.id === cid)?.name || "");
        const visible = needle
          ? faqs.filter((f) =>
              trFold(f.question).includes(needle) ||
              trFold(f.answer).includes(needle) ||
              trFold(catName(f.category_id)).includes(needle))
          : faqs;
        if (visible.length === 0)
          return (
            <div className="border border-slate-200 bg-white px-4 py-14 text-center text-slate-500" data-testid="faq-search-empty">
              Aramanızla eşleşen soru bulunamadı.
            </div>
          );
        return (
        <div className="space-y-2">
          {visible.map((f) => {
            const i = faqs.indexOf(f);
            return (
            <div
              key={f.id}
              draggable={!needle}
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
              onDragLeave={() => setOverIdx((o) => (o === i ? null : o))}
              onDrop={() => onDrop(i)}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              data-testid={`faq-row-${f.id}`}
              className={`border bg-white p-4 flex items-start gap-3 ${needle ? "" : "cursor-grab active:cursor-grabbing"} transition-colors duration-150 ${
                overIdx === i && dragIdx !== null && dragIdx !== i ? "border-slate-900 border-dashed" : "border-slate-200"
              } ${dragIdx === i ? "opacity-50" : ""}`}
            >
              <GripVertical size={16} className="text-slate-300 mt-1 shrink-0" />
              <Checkbox checked={selected.includes(f.id)} onCheckedChange={() => toggleSelect(f.id)} data-testid={`select-faq-${f.id}`} className="mt-1 rounded-none shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                  <span className="text-slate-400 font-heading">{i + 1}.</span>
                  <span data-testid={`faq-question-${f.id}`}>{f.question}</span>
                  <Badge variant="outline" className={`rounded-none text-[10px] font-medium ${audienceCls[f.audience || "supplier"]}`} data-testid={`faq-audience-${f.id}`}>
                    {AUDIENCES[f.audience || "supplier"]}
                  </Badge>
                  {catName(f.category_id) && (
                    <Badge variant="outline" className="rounded-none text-[10px] font-medium border-slate-200 bg-slate-50 text-slate-500" data-testid={`faq-category-${f.id}`}>
                      {catName(f.category_id)}
                    </Badge>
                  )}
                  {f.on_landing && (
                    <Badge variant="outline" className="rounded-none text-[10px] font-medium border-indigo-300 text-indigo-600 gap-1" data-testid={`faq-landing-badge-${f.id}`}>
                      <Megaphone size={10} /> Tanıtımda
                    </Badge>
                  )}
                  {maxViews > 0 && (f.views || 0) === maxViews && (
                    <Badge variant="outline" className="rounded-none text-[10px] font-medium border-orange-300 text-orange-600 gap-1" data-testid={`faq-popular-${f.id}`}>
                      <Flame size={10} /> En çok merak edilen
                    </Badge>
                  )}
                  {(f.down || 0) > (f.up || 0) && (
                    <Badge variant="outline" className="rounded-none text-[10px] font-medium border-red-300 text-red-600 gap-1" data-testid={`faq-weak-${f.id}`}>
                      <ThumbsDown size={10} /> Zayıf cevap
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-slate-500 mt-1 whitespace-pre-wrap">{f.answer}</div>
                <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-2">
                  <span className="flex items-center gap-1" data-testid={`faq-views-${f.id}`}><Eye size={12} /> {f.views || 0} görüntülenme</span>
                  <span className="flex items-center gap-1 text-emerald-600" data-testid={`faq-up-${f.id}`}><ThumbsUp size={12} /> {f.up || 0}</span>
                  <span className="flex items-center gap-1 text-red-500" data-testid={`faq-down-${f.id}`}><ThumbsDown size={12} /> {f.down || 0}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Select value={f.category_id || "none"} onValueChange={(v) => moveCategory(f, v)}>
                  <SelectTrigger className="rounded-none h-8 border-slate-200 text-xs w-[150px] bg-slate-50" data-testid={`move-category-${f.id}`} onClick={(e) => e.stopPropagation()}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="none">Kategorisiz</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`move-category-option-${f.id}-${c.id}`}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => toggleLanding(f)} data-testid={`toggle-landing-${f.id}`} className={`rounded-none h-8 transition-colors duration-200 ${f.on_landing ? "border-indigo-400 bg-indigo-50 text-indigo-600 hover:bg-indigo-100" : "border-slate-200 text-slate-400 hover:bg-slate-100"}`} title={f.on_landing ? "Tanıtım sayfasından kaldır" : "Tanıtım sayfasında göster"}>
                  <Megaphone size={13} />
                </Button>
                <Button variant="outline" size="sm" onClick={() => openEdit(f)} data-testid={`edit-faq-${f.id}`} className="rounded-none h-8 border-slate-200 hover:bg-slate-100 transition-colors duration-200" title="Düzenle">
                  <Pencil size={13} />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setToDelete(f)} data-testid={`delete-faq-${f.id}`} className="rounded-none h-8 border-red-500/30 text-red-500 hover:bg-red-500/10 transition-colors duration-200" title="Sil">
                  <Trash2 size={13} />
                </Button>
              </div>
            </div>
          );})}
        </div>
        );
      })()}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
              <HelpCircle size={20} /> {editing ? "Soruyu Düzenle" : "Yeni Soru Ekle"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Soru</Label>
              <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Örn: Teklifimi nasıl revize edebilirim?" data-testid="faq-question-input" className="rounded-none border-slate-200 h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>Cevap</Label>
              <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={5} placeholder="Cevabı yazın..." data-testid="faq-answer-input" className="rounded-none border-slate-200" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Kategori</Label>
                <Select value={categoryId || "none"} onValueChange={(v) => setCategoryId(v === "none" ? "" : v)}>
                  <SelectTrigger className="rounded-none border-slate-200 h-11" data-testid="faq-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="none" data-testid="faq-category-none">Kategorisiz</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`faq-category-option-${c.id}`}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Kimler görsün?</Label>
                <Select value={audience} onValueChange={setAudience}>
                  <SelectTrigger className="rounded-none border-slate-200 h-11" data-testid="faq-audience-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="supplier" data-testid="faq-audience-supplier">Sadece Tedarikçiler</SelectItem>
                    <SelectItem value="customer" data-testid="faq-audience-customer">Sadece Müşteriler</SelectItem>
                    <SelectItem value="both" data-testid="faq-audience-both">Her İkisi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={save} disabled={busy} data-testid="save-faq-button" className="rounded-none bg-slate-900 text-white hover:bg-slate-700 w-full h-11 font-semibold transition-colors duration-200">
              {busy ? "Kaydediliyor..." : editing ? "Güncelle" : "Ekle"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent className="bg-white border-slate-200 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading font-bold tracking-tight">Soruyu sil?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.question}" sorusu kalıcı olarak silinecek.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="cancel-delete-faq">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="rounded-none bg-red-500 hover:bg-red-600" data-testid="confirm-delete-faq">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
